import { useEffect, useState } from 'react';
import { Col, getItemWithExpiration, Row } from '../../lib/utils';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getTrainerListAPI } from '../../api/employee';
import { useMutation } from '@tanstack/react-query';
import { storeUserFlow } from '../../api/adminuserflow';
import TrainerManageHeader from '../../components/TrainerManage/TrainerManageHeader';
import { SortType } from '../CommunicationPage';
import TrainerList from '../../components/TrainerManage/TrainerList';
import { CodeValueType } from '../../types/types';
import NormalToggleButton from '../../components/ToggleButton/NormalToggleButton';
import { Body3Regular } from '../../lib/font';
import { colors } from '../../lib/colors';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';
import { useTeamFeatureStatus } from '@/features/team';

const TrainerManagePage = ({ createAlert, onClickAddTrainer, setIsLoading }: any) => {
    const navigate = useNavigate();
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));

    // 초기값 설정
    const [coachSortType, setCoachSortType] = useState<
        CodeValueType<'registrationasc' | 'seniorityasc' | 'membercountdesc' | 'alphabetasc'>
    >({ code: 'registrationasc', value: '등록순' });
    const [coachList, setCoachList] = useState<any>([]);

    const [registerList, setRegisterList] = useState<any>([]);
    const [filterCoachList, setFilterCoachList] = useState<any>([]);

    const [isVisibleDeleteTrainer, setIsVisibleDeleteTrainer] = useState<boolean>(false);

    // PT팀 기능은 프랜차이즈(슈퍼관리자 산하) 센터에만 노출
    const { data: teamFeatureStatus } = useTeamFeatureStatus({ centerId: authObject?.centerId });
    const showTeamBanner = teamFeatureStatus?.enabled === true;

    const onClickToggleButton = () => {
        setIsVisibleDeleteTrainer(!isVisibleDeleteTrainer);
    };

    //  트레이너 리스트 조회 API : (GET)
    const getTrainerList = useQuery(
        /*
         * 🔴 centerId 를 **키에** 넣는다 (2026-09-03 대표 제보).
         *
         * 아래에서 centerId 를 인자로 넘기면서 키에는 안 넣고 있었다. 그래서
         * 같은 PC 에서 계정을 바꿔 로그인하면 정렬값만 같으면 **직전 센터의
         * 강사 목록**이 그대로 재사용됐다. 이 화면이 바로 제보된 화면이다.
         * (앞부분이 같으므로 기존 invalidateQueries(['getTrainerListAPI']) 는 그대로 동작한다)
         */
        ['getTrainerListAPI', authObject?.centerId, coachSortType.value],
        async () => await getTrainerListAPI(authObject.centerId, coachSortType.code, authorization),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    const registeredTrainer = res.data.registeredTrainer ? res.data.registeredTrainer : [];
                    const deletedTrainer = res.data.deletedTrainer ? res.data.deletedTrainer : [];

                    const totalList = [...registeredTrainer, ...deletedTrainer];

                    setRegisterList(registeredTrainer);
                    setCoachList(totalList);
                    // isVisibleDeleteTrainer 상태에 따라 초기 필터링 설정
                    setFilterCoachList(isVisibleDeleteTrainer ? totalList : registeredTrainer);
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: !!authorization && !!!authObject?.testAccount,
            // refetchOnWindowFocus: 'always',
            staleTime: 0,
        },
    );

    const onClickDetailPage = (item: any) => {
        navigate('trainerInfo', {
            state: {
                employeeId: item.employeeId,
                isConnected: item.isConnected,
                isDeleted: item.isDeleted,
            },
        });
    };

    useEffect(() => {
        if (isVisibleDeleteTrainer) {
            setFilterCoachList(coachList);
        } else {
            setFilterCoachList(registerList);
        }
    }, [isVisibleDeleteTrainer, coachList, registerList]);

    // 접속 데이터 측정 API
    const pushUserFlow = useMutation({
        mutationFn: async () => await storeUserFlow('trainerManage', authorization),
    });

    useEffect(() => {
        try {
            if (pushUserFlow) {
                pushUserFlow.mutate();
            }
        } catch (error) {
            console.log(error);
        }
    }, []);

    // 로딩 플래그
    useEffect(() => {
        if (getTrainerList) {
            if (getTrainerList.isLoading) {
                if (authObject?.testAccount) {
                    setIsLoading(false);
                } else {
                    setIsLoading(true);
                }
            } else {
                setIsLoading(false);
            }
        }
    }, [getTrainerList]);

    return (
        <Col style={{ marginTop: 64, alignItems: 'center' }}>
            <Col style={{ width: 1192, marginTop: 48 }}>
                {/* PT팀 관리 진입 — 프랜차이즈(슈퍼관리자 산하) 센터만 노출 */}
                {showTeamBanner && (
                    <Row
                        onClick={() => navigate('/teams')}
                        style={{
                            padding: '14px 18px',
                            marginBottom: 20,
                            background: 'linear-gradient(90deg, #ECF1FC 0%, #FFFFFF 100%)',
                            border: `1px solid ${colors.BLUE_100}`,
                            borderRadius: 10,
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <Row style={{ alignItems: 'center', gap: 12 }}>
                            <span style={{ fontSize: 22 }}>👥</span>
                            <Col style={{ gap: 2 }}>
                                <Row style={{ alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 14, fontWeight: 600, color: colors.BLUE_900 }}>
                                        PT팀 관리
                                    </span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 999, background: '#EF4444', color: '#FFFFFF', fontSize: 13, lineHeight: '18px', fontWeight: 800, padding: '1px 5px' }}>
                                        NEW
                                    </span>
                                </Row>
                                <Body3Regular style={{ color: colors.GREY_400 }}>
                                    강사·판매담당자를 팀으로 묶어 매출을 합산해 볼 수 있습니다.
                                </Body3Regular>
                            </Col>
                        </Row>
                        <span style={{ fontSize: 14, color: colors.PRIMARY_BLUE, fontWeight: 500 }}>
                            팀 관리로 이동 →
                        </span>
                    </Row>
                )}

                <TrainerManageHeader
                    coachSortType={coachSortType}
                    setCoachSortType={setCoachSortType}
                    isVisibleDeleteTrainer={isVisibleDeleteTrainer}
                    onClickToggleButton={onClickToggleButton}
                    onClickAddTrainer={onClickAddTrainer}
                />
                <TrainerList filterCoachList={filterCoachList} onClickDetailPage={onClickDetailPage} />
                <Row style={{ alignItems: 'center', marginTop: 16, marginBottom: 80 }}>
                    <NormalToggleButton isActive={isVisibleDeleteTrainer} callBack={onClickToggleButton} />
                    <Body3Regular style={{ color: colors.LAVEL_3, marginLeft: 12 }}>{'삭제된 강사 보기'}</Body3Regular>
                </Row>
            </Col>
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

export default TrainerManagePage;
